import {Injectable} from '@angular/core';
import {Columns, Event} from 'compob2b-table';
import {translations} from '../../../../compob2b-admin/src/environments/translations.ru';
import {Widget} from '../interfaces/Widget';
import {WidgetType} from '../interfaces/WidgetType';
import {BaseService} from './base.service';
import {ListTable} from './list.table';

interface KeyTitle {
  key: string;
  title: string;
  keyword?: string;
}

interface Config {
  disabledColumns: string[];
  order: string[];
  width: Map<string, string>;
}

class ConfigManager {
  static save(key: string, config: Config): void {
    localStorage.setItem(key, this.serialize(config));
  }

  static load(key: string): Config {
    return this.deserialize(localStorage.getItem(key));
  }

  static delete(key: string) {
    localStorage.removeItem(key);
  }

  private static serialize(config: Config): string {
    return JSON.stringify({
      ...config,
      width: Array.from(config.width.entries())
    });
  }

  private static deserialize(json: string): Config {
    const parsed = JSON.parse(json);

    return {
      disabledColumns: parsed?.disabledColumns || [],
      order: parsed?.order || [],
      width: parsed?.width ? new Map(parsed.width) : new Map()
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class ListTableService extends BaseService {
  list: ListTable;
  widgetTypes: Widget[];
  config: Config;

  private columnsSetting: Columns[];

  initFrom(list: ListTable) {
    if (!list.columns) {
      console.error(this.list);
      throw new Error('No cols');
    }

    this.list = list;
    this.columnsSetting = this.list.columns;
    this.config = ConfigManager.load(this.localStorageSettingsKey);

    try {
      this.columnsSetting = this.reorderColumns<Columns>(this.columnsSetting, this.config.order);

      this.config.width.forEach((value: string, key: string) => {
        this.columnsSetting.forEach(_ => {
          if (_.key == key) {
            _.width = value;
          }
        });
      });
    } catch (e) {
      console.error(e);
    }

    if (!this.list.environment.isOpenCabinet) {
      this.initWidgets();
    }
    this.onUpdate();
  }

  getColumns(): Columns[] {
    return this.columnsSetting;
  }

  reset() {
    ConfigManager.delete(this.localStorageSettingsKey);
    window.location.reload();
  }

  saveConfig(event: any = null) {
    if (event?.event == Event.onColumnResize) {
      if (event.data?.key) {
        this.config.width.set(event.data.key, event.data?.width);
      }
    }

    if (event?.dropPoint) {
      this.config.order = [];
      this.columnsSetting.forEach(_ => {
        this.config.order[this.config.order.length] = _.key;
      });
    }

    ConfigManager.save(this.localStorageSettingsKey, this.config);
  }

  private onUpdate() {
    this.columnsSetting.forEach(column => {
      column.enabled = !this.config.disabledColumns.includes(column.key);
    });

    this.list.columns = this.columnsSetting.filter((compoColumn: Columns) => compoColumn.enabled === true);
  }

  get localStorageSettingsKey(): string {
    return ('lst_' + this.list?.constructor?.name + '_' + window.location.pathname.replace(/\d+/, 'xxx'))
      .replace('cabinet/pim/catalog/xxx/products', 'cabinet/pim/catalog');
  }

  setColumn(column: Columns, event: boolean) {
    if (event === false) {
      this.disableColumn(column.key);
    } else {
      this.enableColumn(column.key);
    }
    this.onUpdate();
  }

  disableColumn(compoColumn: string) {
    this.config.disabledColumns.push(compoColumn);
    this.saveConfig();
  }

  enableColumn(compoColumn: string) {
    this.config.disabledColumns = this.config.disabledColumns.filter(item => item !== compoColumn);
    this.saveConfig();
  }

  getWidgets(): Widget[] {
    return JSON.parse(localStorage.getItem('widgets')) || new Array<Widget>();
  }

  writeWidgetConfig(config: Widget[]): void {
    localStorage.setItem('widgets', JSON.stringify(config));
  }

  initWidgets(): void {
    const crudConfig = {title: this.list.title, columns: this.sliceConfig(this.list)};

    this.widgetTypes = [];
    this.widgetTypes.push({
      type: WidgetType.SIMPLE_TABLE,
      title: translations.simpleTable,
      uri: this.list.uriView,
      config: crudConfig,
      visible: true
    });

    this.widgetTypes.push({
      type: WidgetType.PIVOT_TABLE,
      title: translations.pivotTable,
      uri: this.list.uriView,
      config: crudConfig,
      visible: true
    });

    this.widgetTypes.push({
      type: WidgetType.LINEAR_CHART,
      title: translations.linearChart,
      uri: this.list.uriView,
      config: crudConfig,
      visible: true
    });
  }

  registerCustomWidget(widget: Widget): void {
    widget.config = {title: this.list.title, columns: this.sliceConfig(this.list)};
    this.widgetTypes.push(widget);
  }

  private sliceConfig(list: ListTable): KeyTitle[] {
    const restrictedTypes = ['select', 'multicheckbox'];

    return list.columns.filter(item => !restrictedTypes.includes(item.type) && item.grid === true)
      .map(item => this.sliceProps(item));
  }

  private sliceProps(item: Columns): KeyTitle {
    return {key: item.key, title: item.title};
  }

  moveColumn(fromIndex: number, toIndex: number) {
    const cnf = this.columnsSetting[fromIndex];
    this.columnsSetting.splice(fromIndex, 1);
    this.columnsSetting.splice(toIndex, 0, cnf);
    this.onUpdate();
  }

  reorderColumns<T extends { key: string }>(items: T[], order: string[]): T[] {
    if (!order.length) {
      return items;
    }

    const priorityMap = new Map<string, number>();
    order.forEach((key, index) => priorityMap.set(key, index));

    return [...items].sort((a, b) => {
      const aIndex = priorityMap.has(a.key) ? priorityMap.get(a.key) : Number.MAX_SAFE_INTEGER;
      const bIndex = priorityMap.has(b.key) ? priorityMap.get(b.key) : Number.MAX_SAFE_INTEGER;

      return aIndex - bIndex;
    });
  }

}
